import { Toast } from '@jablotron/lib.spa-components';
import { addBreadcrumb, LoggerWrapper, setContext } from '@jablotron/lib.spa-sentry';
import type { PropsWithChildren } from 'react';
import { ErrorBoundary } from './components/boundaries/ErrorBoundary';
import { LanguageProvider, type LanguageProviderProps } from './components/language/LanguageProvider';
import type { UrlLanguage } from './entities/urls/url-language';
import { useHydrated } from './utils/useHydrated';

type AppWrappersProps = PropsWithChildren<{
  /** The router's `urlLanguage`: the UI shows the language of the address (LanguageProvider). */
  urlLanguage?: UrlLanguage;
  /** The server render's texts of the page's language (LanguageProvider renders at once with them). */
  resources?: LanguageProviderProps['resources'];
}>;

/**
 * lib.spa-components' Toast places itself with @uidotdev/usehooks' useMediaQuery, which throws on the server and in
 * the render that hydrates server HTML ("useMediaQuery is a client-only hook"); it has nothing to show there anyway.
 */
const ToastAfterHydration: React.FC = () => (useHydrated() ? <Toast /> : null);

export const AppWrappers: React.FC<AppWrappersProps> = ({ urlLanguage, resources, children }) => (
  <LoggerWrapper onUserEvent={[({ inner: _, ...rest }) => addBreadcrumb(rest)]} onChangeContext={[setContext]}>
    <LanguageProvider urlLanguage={urlLanguage} resources={resources}>
      <ErrorBoundary>
        {children}
        <ToastAfterHydration />
      </ErrorBoundary>
    </LanguageProvider>
  </LoggerWrapper>
);
